import { Plus } from "lucide-react";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";

import { PanelLoading } from "~/components/loading";
import { Button, Checkbox } from "~/components/ui";
import type { ReferenceListFilters } from "~/features/references/types";
import { ReferenceFormDialog } from "./ReferenceFormDialog";
import { ReferencePeek } from "./ReferencePeek";
import { ReferencesTable } from "./ReferencesTable";

interface ReferencesPresenterProps {
  filters: ReferenceListFilters;
  onExcludeDeactivatedChange: (next: boolean) => void;
  onSelect: (id: string) => void;
  selectedReference: string | null;
  onCloseSelected: () => void;
  createOpen: boolean;
  onOpenCreate: () => void;
  onCloseCreate: () => void;
}

export function ReferencesPresenter({
  filters,
  onExcludeDeactivatedChange,
  onSelect,
  selectedReference,
  onCloseSelected,
  createOpen,
  onOpenCreate,
  onCloseCreate,
}: ReferencesPresenterProps) {
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-dewey-title capitalize text-fg-1">{t("model.reference.modelNames")}</h1>
        <div className="flex items-center gap-4">
          <label htmlFor="ref-exclude-deactivated" className="flex items-center gap-2 text-[13px] text-fg-1">
            <Checkbox
              id="ref-exclude-deactivated"
              checked={filters.excludeDeactivated}
              onCheckedChange={(checked) => onExcludeDeactivatedChange(checked === true)}
            />
            <span>{t("feature.common.excludeDeactivated")}</span>
          </label>
          <Button type="button" size="sm" onClick={onOpenCreate}>
            <Plus />
            <span>{t("ui.add")}</span>
          </Button>
        </div>
      </div>

      <Suspense fallback={<PanelLoading />}>
        <ReferencesTable filters={filters} onSelect={onSelect} />
      </Suspense>

      <ReferencePeek id={selectedReference} onClose={onCloseSelected} />
      <ReferenceFormDialog open={createOpen} onOpenChange={(next) => (next ? onOpenCreate() : onCloseCreate())} />
    </div>
  );
}
